/**
 * Qué falta por completar.
 *
 * Una guía puede guardarse incompleta —en planta no siempre se tiene todo a
 * mano— pero lo que falta tiene consecuencias distintas, y algunas cuestan
 * plata:
 *
 *  - Sin partida del contrato, la guía no entra al estado de pago. Se
 *    despachó y no se cobra.
 *  - Sin los seis horarios no hay cómo calcular la espera, así que no se
 *    puede cobrar sobrestadía. En el libro histórico eso alcanzaba al 20% de
 *    las guías.
 *  - Sin cliente o sin centro de costo, CODELCO puede rechazar la línea.
 *
 * Por eso no basta con un contador: cada falta se informa aparte, con qué
 * significa y qué guías la tienen, para poder ir a arreglarlas.
 */

import type { UnidadCantidad } from './unidades';

/** Lo mínimo que hace falta saber de una guía para revisarla. */
export interface GuiaRevisable {
  id: string;
  folio: number;
  fecha: string;
  plantaCodigo: string;
  estado: string;
  itemContratoCodigo?: string | null;
  clienteNombre?: string | null;
  productoCodigo?: string | null;
  centroCostoCodigo?: string | null;
  destinoTexto?: string | null;
  tiemposCompletos?: boolean;
  cantidadM3?: number;
  unidadCantidad?: UnidadCantidad;
  /** De dónde vino. Lo cargado desde Excel es lo que más suele venir cojo. */
  origen?: string;
}

export interface Contexto {
  /** Hoy, para detectar fechas futuras. */
  hoy: string;
  /** Antes de esto ninguna guía es plausible: el contrato no existía. */
  inicioContrato: string;
}

export type Gravedad = 'COBRO' | 'RESPALDO';

export interface Pendiente {
  clave: string;
  titulo: string;
  /** Qué pasa si no se corrige. En una frase. */
  consecuencia: string;
  gravedad: Gravedad;
  guias: GuiaRevisable[];
}

/** Las guías anuladas no se revisan: no se cobran por definición. */
const vigente = (g: GuiaRevisable) => g.estado !== 'ANULADA';

interface Regla {
  clave: string;
  titulo: string;
  consecuencia: string;
  gravedad: Gravedad;
  falta: (g: GuiaRevisable, c: Contexto) => boolean;
}

const REGLAS: Regla[] = [
  {
    clave: 'fechaImposible',
    titulo: 'Guías con fecha imposible',
    consecuencia: 'Una celda mal escrita en el Excel. Caen en el período equivocado, o en ninguno.',
    gravedad: 'COBRO',
    // El caso real: un folio quedó con 04-01-1900 por un tipeo en la planilla.
    falta: (g, c) => g.fecha < c.inicioContrato || g.fecha > c.hoy,
  },
  {
    clave: 'sinPartida',
    titulo: 'Guías sin partida del contrato',
    consecuencia: 'No entran al estado de pago: se despacharon y no se cobran.',
    gravedad: 'COBRO',
    falta: (g) => !g.itemContratoCodigo,
  },
  {
    clave: 'sinCantidad',
    titulo: 'Guías sin cantidad',
    consecuencia: 'Valorizan en cero, así que no aportan nada al estado de pago.',
    gravedad: 'COBRO',
    falta: (g) => !g.cantidadM3,
  },
  {
    clave: 'sinHorarios',
    titulo: 'Guías sin los seis horarios',
    consecuencia: 'Sin marcas no hay cómo calcular la espera: no se puede cobrar sobrestadía.',
    gravedad: 'COBRO',
    falta: (g) => g.tiemposCompletos === false,
  },
  {
    clave: 'sinProducto',
    titulo: 'Guías sin producto',
    consecuencia: 'No se les aplican recargos por aire, resistencia temprana ni cono.',
    gravedad: 'COBRO',
    falta: (g) => !g.productoCodigo,
  },
  {
    clave: 'sinCliente',
    titulo: 'Guías sin cliente',
    consecuencia: 'CODELCO puede rechazar la línea por falta de respaldo.',
    gravedad: 'RESPALDO',
    falta: (g) => !g.clienteNombre,
  },
  {
    clave: 'sinCentroCosto',
    titulo: 'Guías sin centro de costo',
    consecuencia: 'Sin grafo no se puede imputar el gasto al área que lo pidió.',
    gravedad: 'RESPALDO',
    falta: (g) => !g.centroCostoCodigo,
  },
  {
    clave: 'sinDestino',
    titulo: 'Guías sin destino',
    consecuencia: 'Queda sin constancia de dónde se descargó.',
    gravedad: 'RESPALDO',
    falta: (g) => !g.destinoTexto,
  },
  {
    clave: 'borrador',
    titulo: 'Guías en borrador',
    consecuencia: 'Mientras no se emitan quedan fuera del estado de pago.',
    gravedad: 'COBRO',
    falta: (g) => g.estado === 'BORRADOR',
  },
];

/**
 * Revisa las guías y devuelve sólo las faltas que existen, las que impiden
 * cobrar primero.
 */
export function revisarPendientes(
  guias: GuiaRevisable[],
  contexto: Partial<Contexto> = {},
): Pendiente[] {
  const c: Contexto = {
    hoy: contexto.hoy ?? new Date().toISOString().slice(0, 10),
    inicioContrato: contexto.inicioContrato ?? '2022-01-01',
  };
  const vigentes = guias.filter(vigente);

  return REGLAS.map((r) => ({
    clave: r.clave,
    titulo: r.titulo,
    consecuencia: r.consecuencia,
    gravedad: r.gravedad,
    guias: vigentes
      .filter((g) => r.falta(g, c))
      .sort((a, b) => b.fecha.localeCompare(a.fecha) || b.folio - a.folio),
  }))
    .filter((p) => p.guias.length > 0)
    .sort((a, b) => {
      if (a.gravedad !== b.gravedad) return a.gravedad === 'COBRO' ? -1 : 1;
      return b.guias.length - a.guias.length;
    });
}

/** Cuántas guías distintas tienen algo pendiente. */
export function cuantasConPendientes(pendientes: Pendiente[]): number {
  return new Set(pendientes.flatMap((p) => p.guias.map((g) => g.id))).size;
}
